import { useState, useEffect, useRef } from 'react';
import { supabase } from '@bright-web-skeleton/supabase';
import { useUser } from './useUser';

export type AppRole = 'admin' | 'brand' | 'member' | 'customer' | null;

/**
 * Hook to determine user's role by checking role tables.
 * Uses RPC functions (SECURITY DEFINER) where available to bypass RLS.
 * 
 * CRITICAL: This hook is idempotent - it only fetches role ONCE per page load
 * after authReady becomes true. It does NOT re-fetch on user object changes.
 */
export function useRole() {
  const { user, authReady } = useUser();
  const [role, setRole] = useState<AppRole>(null);
  const [loading, setLoading] = useState(true);
  
  // CRITICAL: Prevent duplicate role fetches during auth event churn
  const roleFetchedRef = useRef(false);

  useEffect(() => {
    // AUTH INVARIANT: Gate by authReady - only proceed after auth initialization is complete
    if (!authReady) return;
    
    // Handle no user case
    if (!user) {
      setRole(null);
      setLoading(false);
      return;
    }
    
    // CRITICAL: Only fetch role ONCE per page load
    if (roleFetchedRef.current) {
      return;
    }
    roleFetchedRef.current = true;
    
    // Capture user.id at effect execution time
    const userId = user.id;

    async function fetchRole() {
      if (!userId) {
        setRole(null);
        setLoading(false);
        return;
      }

      console.log('[useRole] Fetching role (ONCE)');
      
      try {
        // 1. Check if user is admin using RPC (bypasses RLS)
        const { data: isAdmin, error: adminRpcError } = await supabase.rpc('is_super_admin', { 
          _user_id: userId 
        });
        
        if (!adminRpcError && isAdmin === true) {
          setRole('admin');
          setLoading(false);
          return;
        }

        // 2. Check if user is brand
        const { data: brandData } = await supabase
          .from('brands')
          .select('id')
          .eq('user_id', userId)
          .limit(1)
          .maybeSingle();

        if (brandData) {
          setRole('brand');
          setLoading(false);
          return;
        }

        // 3. Check if user is member
        const { data: memberData } = await supabase
          .from('members')
          .select('id')
          .eq('user_id', userId)
          .maybeSingle();

        if (memberData) {
          setRole('member');
          setLoading(false);
          return;
        }

        // 4. Check if user is customer
        const { data: customerData } = await supabase
          .from('customers')
          .select('id')
          .eq('user_id', userId)
          .maybeSingle();

        if (customerData) {
          setRole('customer');
          setLoading(false);
          return;
        }

        // Default to null if no role found
        setRole(null);
        setLoading(false);
      } catch (error) {
        console.error('[useRole] Error fetching role:', error);
        setRole(null);
        setLoading(false);
      }
    }

    fetchRole();
  }, [authReady]); // eslint-disable-line react-hooks/exhaustive-deps -- AUTH INVARIANT: user read inside guard

  const hasRole = (requiredRole: AppRole) => role === requiredRole;
  const hasAnyRole = (roles: AppRole[]) => role !== null && roles.includes(role);

  return { role, loading, hasRole, hasAnyRole };
}
